import { apiRequest } from '@/lib/httpClient'
import type {
  PlatformConnectionRow,
  PlatformCreatePayload,
  PlatformEditPayload,
  PlatformStatus,
  PlatformType,
} from './types'

type PlatformGridApiRow = {
  platformId: string
  clientId?: string
  name: string
  companyName?: string
  typeId: number
  statusId: number
  stage?: string
  scheduleFrequencyId?: number
  lastSuccessfulRefresh?: string
  isAuthenticated?: boolean
  hasRefreshToken?: boolean
  hasApiKey?: boolean
  hasApiSecret?: boolean
  hasOtherCredentials?: boolean
  apiKey?: string
  apiSecret?: string
  username?: string
  password?: string
  account?: string
  warehouse?: string
  // The grid sends this to everyone, not just DI admins: the trial connection
  // limit is counted from it on pages a client admin sees.
  supportAdvisePrep?: boolean | null
}

type PlatformEditInitApiRow = PlatformGridApiRow & {
  supportAdvisePrep?: boolean | null
}

type PlatformGridApiResponse = {
  platforms?: PlatformGridApiRow[]
  availableCredits?: number
}

type EditInitResponse = {
  platform?: PlatformEditInitApiRow
  isAuthenticated?: boolean
}

const typeIdToName: Record<number, PlatformType> = {
  1: 'Xero',
  2: 'XPM',
  4: 'MYOB',
  5: 'WorkGuru',
  6: 'BRWFM',
  7: 'Karbon',
  8: 'FYI',
  9: 'QuickBooks',
}

const typeNameToId: Record<PlatformType, number> = {
  Xero: 1,
  XPM: 2,
  MYOB: 4,
  WorkGuru: 5,
  BRWFM: 6,
  Karbon: 7,
  FYI: 8,
  QuickBooks: 9,
}

const statusIdToName: Record<number, PlatformStatus> = {
  0: 'Pending',
  1: 'Pending',
  2: 'Refreshing',
  3: 'Refreshed',
  4: 'Failed',
  5: 'Connected',
}

const statusNameToId: Record<PlatformStatus, number> = {
  Pending: 1,
  Refreshing: 2,
  Refreshed: 3,
  Failed: 4,
  Connected: 5,
}

function toUiRow(row: PlatformGridApiRow): PlatformConnectionRow {
  return {
    id: row.platformId,
    name: row.name,
    clientName: row.name,
    companyName: row.companyName || '',
    type: typeIdToName[row.typeId] || 'Xero',
    status: statusIdToName[row.statusId] || 'Pending',
    frequency: row.scheduleFrequencyId === 1 ? 'Daily' : 'Manual',
    lastSuccessfulRefresh: row.lastSuccessfulRefresh || null,
    stage: row.stage,
    isAuthenticated: Boolean(row.isAuthenticated),
    hasRefreshToken: Boolean(row.hasRefreshToken),
    hasApiKey: Boolean(row.hasApiKey),
    hasApiSecret: Boolean(row.hasApiSecret),
    hasOtherCredentials: Boolean(row.hasOtherCredentials),
    apiKey: row.apiKey,
    apiSecret: row.apiSecret,
    username: row.username,
    password: row.password,
    account: row.account,
    warehouse: row.warehouse,
    // Kept nullish rather than coerced: Boolean(undefined) is false, which would
    // make "the grid did not send this" indistinguishable from "not billed" and
    // let the trial counter report a confident zero.
    supportAdvisePrep: row.supportAdvisePrep ?? undefined,
  }
}

export type FetchPlatformConnectionsParams = {
  clientId: string
  name?: string
  companyName?: string
  type?: PlatformType | 'all'
  status?: PlatformStatus | 'all'
}

export async function fetchPlatformConnections(
  params: FetchPlatformConnectionsParams
): Promise<PlatformConnectionRow[]> {
  const payload = await apiRequest<PlatformGridApiResponse>('Platform/ui/list/grid', {
    method: 'GET',
    query: {
      clientId: params.clientId,
      filterByCompanyId: false,
    },
  })

  let rows = (payload.platforms || []).map(toUiRow)

  if (params.name?.trim()) {
    const q = params.name.trim().toLowerCase()
    rows = rows.filter((row) => row.name.toLowerCase().includes(q))
  }

  if (params.companyName?.trim()) {
    const q = params.companyName.trim().toLowerCase()
    rows = rows.filter((row) => row.companyName.toLowerCase().includes(q))
  }

  if (params.type && params.type !== 'all') {
    rows = rows.filter((row) => row.type === params.type)
  }

  if (params.status && params.status !== 'all') {
    rows = rows.filter((row) => row.status === params.status)
  }

  return rows
}

export async function initPlatformEdit(platformId: string): Promise<PlatformConnectionRow | null> {
  const payload = await apiRequest<EditInitResponse>('Platform/ui/edit/initialise', {
    method: 'GET',
    query: { platformId },
  })

  if (!payload.platform) return null
  const row = toUiRow({
    ...payload.platform,
    isAuthenticated: payload.isAuthenticated ?? payload.platform.isAuthenticated,
  })
  // Anything but a real boolean means the server withheld it, so it must stay
  // undefined — a null would pass the modal's presence check.
  const supportAdvisePrep = payload.platform.supportAdvisePrep
  return typeof supportAdvisePrep === 'boolean' ? { ...row, supportAdvisePrep } : row
}

export async function updatePlatform(payload: PlatformEditPayload & { clientId: string; status?: PlatformStatus }) {
  await apiRequest('Platform/ui/edit/save', {
    method: 'POST',
    body: {
      platformId: payload.id,
      clientId: payload.clientId,
      name: payload.name,
      typeId: typeNameToId[payload.type],
      statusId: payload.status ? statusNameToId[payload.status] : undefined,
      scheduleFrequencyId: 1,
      apiKey: payload.apiKey,
      apiSecret: payload.apiSecret,
      username: payload.username,
      password: payload.password,
      account: payload.account,
      warehouse: payload.warehouse,
      // Left undefined it is dropped from the body, which the API reads as "leave unchanged".
      supportAdvisePrep: payload.supportAdvisePrep,
    },
  })
}

export async function createPlatform(payload: PlatformCreatePayload & { clientId: string }) {
  await apiRequest('Platform/ui/create/save', {
    method: 'POST',
    body: {
      clientId: payload.clientId,
      name: payload.name,
      typeId: typeNameToId[payload.type],
      scheduleFrequencyId: 1,
      apiKey: payload.apiKey,
      apiSecret: payload.apiSecret,
      username: payload.username,
      password: payload.password,
      account: payload.account,
      warehouse: payload.warehouse,
    },
  })
}

export async function disconnectSinglePlatform(
  platformId: string,
  isBulk: boolean
): Promise<{ name?: string; success?: boolean }> {
  return apiRequest(`Platform/ui/edit/disconnect/${platformId}/${isBulk}`, {
    method: 'POST',
    body: {},
  })
}

export async function disconnectPlatforms(platformIds: string[], isBulk: boolean) {
  await Promise.all(
    platformIds.map((platformId) =>
      apiRequest(`Platform/ui/edit/disconnect/${platformId}/${isBulk}`, {
        method: 'POST',
        body: {},
      })
    )
  )
}

export async function deletePlatform(platformId: string) {
  await apiRequest(`Platform/ui/edit/delete/${platformId}`, {
    method: 'POST',
    body: {},
  })
}

export async function manualRefreshPlatform(input: {
  platformId: string
  typeId: number
  date?: string | null
  ledgerStandalone?: boolean
}) {
  await apiRequest('Platform/ui/refresh', {
    method: 'POST',
    body: {
      platformId: input.platformId,
      typeId: input.typeId,
      date: input.date || null,
      ledgerStandalone: input.ledgerStandalone ?? false,
    },
  })
}

export function platformTypeToId(type: PlatformType) {
  return typeNameToId[type]
}
